/**
 * OnboardingStepIndicator — shared 5-step visual system used across every
 * onboarding screen (setup → legal → [trial] → install → notifications →
 * ready). Keeps the numbered "Step X of 5" label and the coloured segmented
 * progress bar identical for every screen so the flow feels consistent.
 *
 * Usage:
 *   <OnboardingStepIndicator current={2} role="worker" />
 *
 * Props:
 *   - current: 1..5 (required) — which step is active/complete
 *   - role: 'manager' | 'worker' — controls accent colour
 *   - total: default 5, override only if a screen truly needs another count
 */
export default function OnboardingStepIndicator({ current, role = 'manager', total = 5 }) {
  const accent = role === 'worker' ? '#DA291C' : '#0f1b3d';
  const step = Math.max(1, Math.min(current, total));
  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <span
          className="text-sm font-semibold text-slate-500"
          data-testid="step-indicator"
        >
          Step {step} of {total}
        </span>
      </div>
      <div className="flex gap-2 mb-7" data-testid="step-progress-bar">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className="h-1.5 flex-1 rounded-full"
            style={{ backgroundColor: i < step ? accent : '#e2e8f0' }}
            data-testid={`step-progress-seg-${i + 1}`}
          />
        ))}
      </div>
    </>
  );
}
